<template>
  <SubHeading :home="home" :tabs="tabs" :shorts="false" :currTab="currTab" @active="handleActive" />

  <!-- breadcrumbs -->
  <nav class="sba-collection-breadcrumb z-depth-0">
    <div class="container nav-wrapper">
      <div class="sba-collection-breadcrumb-row">
        <router-link to="/" class="breadcrumb sba-collection-crumb teal-text">
          <i class="material-icons">home</i>
          <span>Dashboard</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <router-link to="/retail" class="breadcrumb sba-collection-crumb blue-grey-text text-darken-3">
          <span>Retail Home</span>
        </router-link>
        <i class="material-icons sba-collection-crumb-separator">chevron_right</i>
        <span class="breadcrumb sba-collection-crumb blue-grey-text pointable">
          <span>View</span>
        </span>
        <span class="sba-collection-context">Retail Client Workspace</span>
      </div>
    </div>
  </nav>

  <div id="top"></div>

  <div class="sba-collection-page-surface is-workbench retail-secondary-surface">
    <div class="container">
      <LoadingDiv v-if="!isReady" />

      <div class="client-workbench sba-slide-in" v-else>
        <section class="client-secondary-header">
          <section class="client-super-card">
            <button type="button" class="secondary-page-back" title="Back to All Retail Clients"
              @click.prevent="handleActive(home)">
              <i class="material-icons">keyboard_return</i>
            </button>
            <div class="client-super-copy">
              <button type="button" class="secondary-parent-link" @click.prevent="handleActive(home)">
                All Retail Clients
              </button>
              <small>Back to retail client list</small>
            </div>
          </section>

          <section class="client-dossier-card">
            <div class="client-dossier-title">
              <label class="active teal-text">Retail Client Workspace</label>
              <h5>{{ capWords(client?.name || "Client") }}</h5>
              <p>
                Manage this retail client account and its member records.
              </p>
            </div>

            <div class="client-dossier-context">
              <span class="context-pill" :class="clientStatusClass">{{ clientStatusLabel }}</span>
              <span class="context-pill">Retail client</span>
              <span class="context-pill">{{ clientContactLabel }}</span>
            </div>
          </section>

          <section class="client-mode-strip" :class="{ 'has-submode': visibleSubTabs.length }">
            <button type="button" class="client-home-icon-action" title="Back to All Retail Clients" @click.prevent="handleActive(home)">
              <i class="material-icons">close</i>
            </button>
            <div class="client-mode-actions">
              <button
                v-for="tab in tabsMenu"
                :key="tab.id"
                type="button"
                class="client-mode-option"
                :class="{ 'is-active': currTabMenu?.title === tab.title }"
                @click.prevent="handleActiveMenu(tab)"
              >
                {{ tab.title }}
              </button>
            </div>
          </section>

          <section class="client-submode-strip" v-if="visibleSubTabs.length">
            <div class="client-submode-copy">
              <span>{{ subModeTitle }}</span>
              <small>{{ subModeDescription }}</small>
            </div>
            <div class="client-submode-actions">
              <button
                v-for="tab in visibleSubTabs"
                :key="tab.id"
                type="button"
                class="client-submode-option"
                :class="{ 'is-active': currTabSmall === tab.title, 'is-entry-link': subTabIcon(tab) }"
                @click.prevent="handleActiveMenu(tab)"
              >
                <i v-if="subTabIcon(tab)" class="material-icons client-submode-icon">{{ subTabIcon(tab) }}</i>
                <span>{{ subTabLabel(tab) }}</span>
                <small v-if="subTabCount(tab) !== null">{{ subTabCount(tab) }}</small>
              </button>
            </div>
          </section>
        </section>

        <section
          class="client-secondary-body-frame"
          :class="{
            'is-principal-section': currTabSmall?.includes('Principals'),
            'is-principal-detail': principalDetailOpen,
          }"
        >
          <LoadingDiv v-if="!currTabSmall" />

          <!-- Contract -->
          <ContractDiv :cat="cat" :clientId="clientId" v-if="currTabSmall == 'Client Contract'" />

          <!-- Status -->
          <StatusDiv :cat="cat" :clientId="clientId" v-if="currTabSmall == 'Status'" />

          <!-- Settings -->
          <SettingsDiv :cat="cat" :clientId="clientId" v-if="currTabSmall == 'Settings'" />

          <!-- Profile -->
          <ProfileDiv :cat="cat" :client="client" @update="handleUpdate" v-if="currTabSmall == 'Profile'" />

          <!-- Policy -->
          <PolicyDiv :cat="cat" :client="client" @reset="handlePolicyReset" v-if="currTabSmall == 'Plans -> All'" />

          <!-- Policy Add -->
          <PolicyAddDiv :cat="cat" :client="client" @reset="handlePolicyAdd" v-if="currTabSmall == 'Plans -> Add'" />

          <!-- Enrollees Lobby -->
          <EnrolleesDiv
            :authLevel="authLevel"
            :status="client?.status"
            :cat="cat"
            :clientId="clientId"
            :client-name="client?.name"
            :displayType="handleSplitForDisplay(currTabSmall)"
            :key="principalSubLinkKey('lobby')"
            v-if="currTabSmall == 'Principals -> Lobby' && hasActivePolicy && hasActivePlan"
          />
          <article class="policy-required-card" v-if="currTabSmall == 'Principals -> Lobby' && !hasActivePolicy">
            <i class="material-icons">event_busy</i>
            <h6>Policy year required</h6>
            <p>Activate a policy year before moving members from the lobby.</p>
          </article>
          <article class="policy-required-card" v-if="currTabSmall == 'Principals -> Lobby' && hasActivePolicy && !hasActivePlan">
            <i class="material-icons">assignment_late</i>
            <h6>Active plan required</h6>
            <p>Add an active plan before moving members from the lobby.</p>
          </article>

          <!-- Enrollees Active -->
          <EnrolleesDiv
            :authLevel="authLevel"
            :status="client?.status"
            :cat="cat"
            :clientId="clientId"
            :client-name="client?.name"
            :displayType="handleSplitForDisplay(currTabSmall)"
            :key="principalSubLinkKey('active')"
            @detail-state="handlePrincipalDetailState"
            v-if="currTabSmall == 'Principals & Dependants -> Active'"
          />

          <!-- Enrollees On-Hold -->
          <EnrolleesDiv
            :authLevel="authLevel"
            :status="client?.status"
            :cat="cat"
            :clientId="clientId"
            :client-name="client?.name"
            :displayType="handleSplitForDisplay(currTabSmall)"
            :key="principalSubLinkKey('hold')"
            @detail-state="handlePrincipalDetailState"
            v-if="currTabSmall == 'Principals & Dependants -> (On-hold)'"
          />

          <!-- Enrollees Orphan -->
          <EnrolleesDiv
            :authLevel="authLevel"
            :status="client?.status"
            :cat="cat"
            :clientId="clientId"
            :client-name="client?.name"
            :displayType="handleSplitForDisplay(currTabSmall)"
            :key="principalSubLinkKey('orphan')"
            @detail-state="handlePrincipalDetailState"
            v-if="currTabSmall == 'Principals & Dependants -> Orphan'"
          />

          <!-- Enrollees Inactive -->
          <EnrolleesDiv
            :authLevel="authLevel"
            :status="client?.status"
            :cat="cat"
            :clientId="clientId"
            :client-name="client?.name"
            :displayType="handleSplitForDisplay(currTabSmall)"
            :key="principalSubLinkKey('bin')"
            v-if="currTabSmall == 'Principals & Dependants -> Bin'"
          />

          <!-- Enrollees Download -->
          <EnrolleeDownloadDiv
            :cat="cat"
            :clientId="clientId"
            :key="principalSubLinkKey('download')"
            v-if="currTabSmall == 'Principals -> Download'"
          />

          <!-- Payment -->
          <PaymentDiv :cat="cat" :clientId="clientId" v-if="currTabSmall == 'Invoices -> All'" />

          <!-- Insight -->
          <ClientInsightDiv :clientId="clientId" @reset="handleInsightReset" v-if="currTabSmall == 'Insight'" />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUpdated, ref, watchEffect } from "vue";
import { useRouter } from "vue-router";
import useModuleNavigation from "@/composables/useModuleNavigation";
import getItem from "@/composables/getItem";
import getItemsOfItems from "@/composables/getItemsOfItems";
import SubHeading from "@/components/HeaderSubDiv.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import ContractDiv from "@/components/client/ContractClientComponent.vue";
import StatusDiv from "@/components/client/StatusClientComponent.vue";
import SettingsDiv from "@/components/client/SettingsClientComponent.vue";
import EnrolleesDiv from "@/components/client/EnrolleeClientComponent.vue";

import EnrolleeDownloadDiv from "@/components/client/download/EnrolleeDownloadComponent.vue";

import ProfileDiv from "@/components/client/ProfileClientComponent.vue";
import PolicyDiv from "@/components/client/PolicyClientComponent.vue";
import PolicyAddDiv from "@/components/client/onboard/PolicyReviewAddOnboardComponent.vue";
import PaymentDiv from "@/components/client/PaymentClientComponent.vue";
import ClientInsightDiv from "@/components/insight/ClientDetailInsightComponent.vue";

import {
  capWordsFx,
} from "@/composables/fxLibrary/coreFx.js";

import M from "materialize-css";


export default {
  name: "retailClientView",
  components: {
    SubHeading,
    LoadingDiv,
    ContractDiv,
    SettingsDiv,
    StatusDiv,
    ProfileDiv,
    PolicyDiv,
    PolicyAddDiv,
    EnrolleesDiv,
    EnrolleeDownloadDiv,
    PaymentDiv,
    ClientInsightDiv,
  },
  props: ["id"],
  setup(props) {
    const home = ref(null);
    const authLevel = 5;
    const cat = ref("retail");
    const isReady = ref(false);
    const response = ref(null);
    const counter = ref({});
    const error = ref(null);
    const currTabMenu = ref(null);
    const tabsMenu = ref([]);
    const currTabSmall = ref(null);
    const tabsSmall = ref([]);
    const clientId = ref(props.id);
    const currTab = ref(null);
    const tabs = ref([]);
    const principalDetailOpen = ref(false);
    const principalSubLinkVersion = ref(0);
    const hasActivePolicy = ref(false);

    // vue router
    const router = useRouter();
    const { routeToTab } = useModuleNavigation();

    // start
    onMounted(() => {
      // page name
      document.title = "View Single Client | EasyHMO";
    });

    // dropdown
    onMounted(() => {
      const elems = document.querySelectorAll(".dropdown-trigger");
      M.Dropdown.init(elems, {});
    });

    onUpdated(() => {
      const elems = document.querySelectorAll(".dropdown-trigger");
      M.Dropdown.init(elems, {});
    });

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Retail (ePlans)", title: "All", icon: "arrow_back", url: "retail", hash: null, });
      tabs.value.push({ id: 2, name: "Retail (ePlans)", title: "View", icon: null, url: null, hash: null });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("View"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    //from functions
    const capWords = (val) => {
      return capWordsFx(val);
    };

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: !tab?.url });
    };

    // Get client
    onMounted(() => {
      // get client details
      getClient();
      // get counters
      getCounter();
    });


    // get client
    const getClient = () => {
      (async () => {
        const { res, err } = await getItem("client", props.id);

        if (res.value) {
          if (res.value.data.cat != cat.value) {
            // refer back home
            router.push({
              name: "home",
            });
          } else {
            // assign
            response.value = res.value.data;
          }
        } else if (err.value) {
          error.value = err.value;
          // back to retail
          router.push({
            name: "retail",
          });
        }

        // page is ready
        isReady.value = true;
      })();
    };

    // get client
    const getCounter = () => {
      (async () => {
        const { res } = await getItem("principal/client/counter", props.id);

        if (res.value) {
          counter.value = res.value.data;
          setTabs();
        }

      })();
    };

    // UI modification
    const maxRule = (text) => {
      let maxLength = 15;
      if (text.length > maxLength) {
        return ` ${text.substring(0, maxLength)} ... `;
      } else {
        return ` ${text} `;
      }
    };

    // handle add
    const handlePolicyAdd = (val) => {
      if (val) {
        // reload
        response.value = val;
        currTabSmall.value = "Plans -> All"; // go back to Plans
      } else {
        currTabSmall.value = null; // restart menu
      }
    };

    // handle reet
    const handlePolicyReset = (val) => {
      if (val) {
        // reload
        response.value = val;
      }
    };

    // handle back
    const handleBack = () => {
      if (!currTabSmall.value) {
        router.push({ name: "retail" }); // final back
      } else {
        currTabSmall.value = null; // restart menu
      }
    };

    // handle split
    const handleSplit = (val, type) => {
      let newVal;
      let parts;
      //set
      if (val.includes("->")) {
        parts = val.split("->").map((s) => s.trim());
      } else {
        parts = [val, null];
      }
      // close
      if (type == "head") {
        newVal = parts[0];
      } else if (type == "body") {
        newVal = parts[1];
      }
      // return
      return newVal;
    };

    // handle split
    const handleSplitForDisplay = (val) => {
      let newVal;
      let parts;
      //set
      if (val.includes("->")) {
        parts = val.split("->").map((s) => s.trim());
        // close
        if (parts[1].toLowerCase() == "active") {
          newVal = "a";
        } else if (parts[1].toLowerCase() == "(on-hold)") {
          newVal = "h";
        } else if (parts[1].toLowerCase() == "orphan") {
          newVal = "o";
        } else if (parts[1].toLowerCase() == "bin") {
          newVal = "d";
        } else if (parts[1].toLowerCase() == "lobby") {
          newVal = "p";
        }
      } else {
        newVal = "a";
      }
      // return
      return newVal;
    };

    // Title
    watchEffect(() => {
      if (response.value) {
        // load title
        setTimeout(() => {
          var elems = document.querySelectorAll(".tooltipped");
          M.Tooltip.init(elems, {});
        }, 2000);
      }
    });

    // handle tab
    const setTabs = () => {
      // empty
      tabsSmall.value = [];
      // load tab
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Status", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Insight", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Client Contract", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Spacer", msg: null, color: null });
      // tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Principals -> Add", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Principals & Dependants -> Active", msg: counter.value?.active, color: 'teal lighten-2' });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Principals -> Lobby", msg: counter.value?.lobby, color: 'yellow lighten-2' });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Principals & Dependants -> (On-hold)", msg: counter.value?.pending, color: 'grey lighten-2' });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Principals & Dependants -> Orphan", msg: counter.value?.orphan, color: 'orange lighten-3' });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Principals & Dependants -> Bin", msg: counter.value?.bin, color: 'red lighten-2' });
      // tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Principals -> Remove", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Principals -> Download", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Spacer", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Plans -> All", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Plans -> Add", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Spacer", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Invoices -> All", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Spacer", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Profile", msg: null, color: null });
      tabsSmall.value.push({ id: tabsSmall.value.length + 1, title: "Settings", msg: null, color: null });

      // update show and components
      setDefaultSmallTab();
    };

    const hasActivePolicyYear = async () => {
      const { res } = await getItemsOfItems("policy/client", props.id);
      const policies = Array.isArray(res.value?.data) ? res.value.data : [];
      const serverDate = res.value?.server || new Date().toISOString().split("T")[0];

      return policies.some((policy) => (
        policy.status == "a" &&
        new Date(policy.end_date || policy.end) > new Date(serverDate)
      ));
    };

    const setDefaultSmallTab = async () => {
      if (currTabSmall.value) return;

      hasActivePolicy.value = await hasActivePolicyYear();
      currTabSmall.value = hasActivePolicy.value ? "Principals & Dependants -> Active" : "Status";
      currTabMenu.value = tabsMenu.value.find(x => currTabSmall.value.toLowerCase().includes(x.title.toLowerCase())) || tabsMenu.value[0];
    };

    // Initiate
    onMounted(() => {
      // load tab
      // tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Menu", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Status", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Insight", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Contract", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Principals", dropdown: true });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Plans", dropdown: true });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Invoices", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Profile", dropdown: false });
      tabsMenu.value.push({ id: tabsMenu.value.length + 1, title: "Settings", dropdown: false });

      // set default
      currTabMenu.value = tabsMenu.value[0];
    });

    // Initiate
    onMounted(() => {
      // set tab
      setTabs();
      // set default
      currTabSmall.value = null;
    });


    // handle curr status
    const handleActiveSmall = (tab) => {
      principalDetailOpen.value = false;
      if (tab?.includes("Principals")) {
        principalSubLinkVersion.value += 1;
      }
      // update show and components
      currTabSmall.value = tab;
      // highlight menu
      if (currTabSmall.value) {
        currTabMenu.value = tabsMenu.value.find(x => currTabSmall.value.toLowerCase().includes(x.title.toLowerCase()));
      } else {
        currTabMenu.value = tabsMenu.value[0];
      }
    };

    // handle curr status
    const handleActiveMenu = (tab) => {
      // check non-drop-downs
      // if (!tab?.dropdown) {
        let newActive = tabsSmall.value.find(x => x.title.toLowerCase().includes(tab.title.toLowerCase()));
        if (newActive) {
          handleActiveSmall(newActive?.title);
        } else {
          handleActiveSmall();
        }
      // } else {
      //   handleActiveSmall();
      // }
    };

    // handle emitted update
    const handleUpdate = (val) => {
      response.value = val;
    };

    const handleInsightReset = () => {
      handleActiveSmall("Status");
    };

    const handlePrincipalDetailState = (val) => {
      principalDetailOpen.value = Boolean(val);
    };

    const principalSubLinkKey = (name) => {
      return `${name}-${principalSubLinkVersion.value}`;
    };

    // output
    const client = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    const firstPremiumAmount = (plan) => {
      return Number(plan?.premiums?.[0]?.amount || 0);
    };

    const isPlanAvailableForMembers = (plan) => {
      if (["d", "s"].includes(plan?.status)) {
        return false;
      }

      return firstPremiumAmount(plan) > 0;
    };

    const hasActivePlan = computed(() => {
      const plans = Array.isArray(client.value?.plans) ? client.value.plans : [];
      return plans.some(isPlanAvailableForMembers);
    });

    const visibleSubTabs = computed(() => {
      if (currTabSmall.value?.includes("Principals")) {
        return tabsSmall.value.filter(x => x.title.includes("Principals"));
      }

      if (currTabSmall.value?.includes("Plan")) {
        return tabsSmall.value.filter(x => x.title.includes("Plan"));
      }

      return [];
    });

    const subModeTitle = computed(() => {
      if (currTabSmall.value?.includes("->")) {
        const head = handleSplit(currTabSmall.value, "head")?.replace("Principals & Dependants", "Principal");
        const body = handleSplit(currTabSmall.value, "body");
        return `${head} ${body}`;
      }

      if (currTabSmall.value?.includes("Principals")) {
        return "Principals";
      }

      if (currTabSmall.value?.includes("Plan")) {
        return "Plans";
      }

      return "Workspace actions";
    });

    const subModeDescription = computed(() => {
      const descriptions = {
        "Principals & Dependants -> Active": "View active principals and their covered family members.",
        "Principals -> Lobby": "Review pending members and activate eligible records.",
        "Principals & Dependants -> (On-hold)": "Review members waiting on billing or cover confirmation.",
        "Principals & Dependants -> Orphan": "Review members without a policy subscription.",
        "Principals & Dependants -> Bin": "Review inactive members and restore records when required.",
        "Principals -> Download": "Download principal and dependant records for this retail client.",
        "Plans -> All": "Review the plans currently attached to this retail client.",
        "Plans -> Add": "Attach a new active plan before activating members.",
      };

      if (descriptions[currTabSmall.value]) {
        return descriptions[currTabSmall.value];
      }

      if (currTabSmall.value?.includes("Principals")) {
        return "Review active, on-hold, removed principal records and related family lists";
      }

      if (currTabSmall.value?.includes("Plan")) {
        return "Review policy plans available to this retail client account";
      }

      return "Select a task";
    });

    const subTabLabel = (tab) => {
      return handleSplit(tab.title, "body") || tab.title;
    };

    const subTabIcon = (tab) => {
      const title = tab?.title || "";

      if (title == "Principals -> Add") {
        return "person_add";
      }

      if (title == "Principals -> Lobby") {
        return "pending_actions";
      }

      return null;
    };

    const subTabCount = (tab) => {
      if (tab.msg === null || tab.msg === undefined) {
        return null;
      }

      return tab.msg;
    };

    const clientStatusLabel = computed(() => {
      if (client.value?.status === "s") {
        return "Inactive client";
      }

      if (client.value?.status === "p") {
        return "Pending approval";
      }

      return "Active client";
    });

    const clientStatusClass = computed(() => {
      if (client.value?.status === "s") {
        return "is-inactive";
      }

      if (client.value?.status === "p") {
        return "is-pending";
      }

      return "is-active";
    });

    const clientContactLabel = computed(() => {
      return client.value?.email || client.value?.phone || "No contact recorded";
    });

    return {
      home,
      capWords,
      handleActive,
      currTab,
      tabs,
      authLevel,
      cat,
      maxRule,
      handleSplit,
      handleSplitForDisplay,
      handleActiveSmall,
      handleActiveMenu,
      handleUpdate,
      handleInsightReset,
      handleBack,
      handlePolicyAdd,
      handlePolicyReset,
      isReady,
      error,
      currTabMenu,
      tabsMenu,
      currTabSmall,
      hasActivePolicy,
      hasActivePlan,
      tabsSmall,
      clientId,
      client,
      principalDetailOpen,
      visibleSubTabs,
      subModeTitle,
      subModeDescription,
      subTabLabel,
      subTabIcon,
      subTabCount,
      clientStatusClass,
      clientStatusLabel,
      clientContactLabel,
      handlePrincipalDetailState,
      principalSubLinkKey,
    };
  },
};
</script>

<style scoped>
.retail-secondary-surface {
  min-height: calc(100vh - 260px);
  padding-bottom: 56px;
}

.client-workbench {
  color: #263238;
  margin-top: -14px;
  padding-top: 30px;
  position: relative;
  z-index: 1;
}

.client-super-card,
.client-dossier-card,
.client-mode-strip,
.client-submode-strip,
.client-secondary-body-frame,
.policy-required-card,
.client-workbench :deep(.principal-card),
.client-workbench :deep(.principal-card-orphan),
.client-workbench :deep(.bin-record),
.client-workbench :deep(.status-head),
.client-workbench :deep(.status-card),
.client-workbench :deep(.history-card),
.client-workbench :deep(.contract-head),
.client-workbench :deep(.contract-card),
.client-workbench :deep(.policy-head),
.client-workbench :deep(.policy-card),
.client-workbench :deep(.benefit-card),
.client-workbench :deep(.invoice-head),
.client-workbench :deep(.invoice-card),
.client-workbench :deep(.invoice-detail),
.client-workbench :deep(.detail-loader),
.client-workbench :deep(.enrollee-search-bar),
.client-workbench :deep(.profile-summary),
.client-workbench :deep(.profile-card),
.client-workbench :deep(.settings-summary),
.client-workbench :deep(.setting-card),
.client-workbench :deep(.empty-state) {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.client-secondary-header {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 16px 30px -26px rgba(38, 50, 56, 0.64);
  overflow: visible;
}

.client-dossier-card {
  align-items: flex-end;
  background: #ffffff;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  padding: 28px 32px 24px;
  position: relative;
}

.client-dossier-title {
  min-width: 0;
}

.secondary-page-signifier {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-bottom-color: #fff8e1;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 -8px 18px -18px rgba(121, 85, 72, 0.5);
  color: #4e342e;
  display: inline-flex;
  gap: 7px;
  font-size: 0.76rem;
  font-weight: 800;
  left: 24px;
  line-height: 1.2;
  padding: 7px 10px;
  position: absolute;
  text-transform: uppercase;
  top: -32px;
}

.secondary-page-back {
  align-items: center;
  background: #00897b;
  border: 1px solid #00796b;
  border-radius: 5px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  height: 24px;
  justify-content: center;
  margin: -2px 1px -2px -3px;
  padding: 0;
  width: 24px;
}

.secondary-page-back i {
  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
}

.secondary-parent-link {
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #4e342e;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
  margin: -2px 0;
  padding: 3px 4px;
  text-transform: uppercase;
  transition: background-color 0.18s ease, color 0.18s ease;
}

.secondary-parent-link:hover {
  background: rgba(255, 213, 79, 0.42);
  color: #263238;
}

.secondary-page-signifier small {
  border-left: 1px solid rgba(121, 85, 72, 0.18);
  color: #6d4c41;
  font-size: 0.72rem;
  font-weight: 700;
  margin-left: 2px;
  padding-left: 9px;
  text-transform: none;
}

.client-dossier-title h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
  overflow-wrap: anywhere;
}

.client-dossier-title p {
  color: #607d8b;
  font-size: 1.02rem;
  line-height: 1.5;
  margin: 9px 0 0;
  max-width: 760px;
}

.client-dossier-context {
  align-items: flex-end;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 8px;
}

.context-pill {
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.2;
  padding: 8px 12px;
  text-align: right;
}

.context-pill.is-active {
  background: #e8f3f2;
  border-color: #9bd2cb;
  color: #00695c;
}

.context-pill.is-inactive {
  background: #ffebee;
  border-color: #ffcdd2;
  color: #b71c1c;
}

.context-pill.is-pending {
  background: #fff8e1;
  border-color: #ffe082;
  color: #795548;
}

.client-mode-strip,
.client-submode-strip {
  align-items: center;
  border-top: 1px solid #edf2f4;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 16px 24px;
}

.client-mode-strip {
  background: #ffffff;
  border-color: #cbd7dc;
  box-shadow: 0 16px 26px -24px rgba(38, 50, 56, 0.85);
  color: #263238;
}

.client-submode-strip {
  background: #f7f7f7;
}

.client-submode-copy {
  display: grid;
  gap: 3px;
}

.client-submode-copy span {
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.client-submode-copy small {
  color: #607d8b;
  font-weight: 700;
}

.client-home-icon-action {
  align-items: center;
  background: #f8fafb;
  border: 1px solid #90a4ae;
  border-radius: 50%;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.92);
  color: #263238;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 34px;
  justify-content: center;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease, transform 0.18s ease;
  width: 34px;
}

.client-home-icon-action:hover {
  background: #e0f2f1;
  border-color: #00897b;
  box-shadow: 0 8px 16px -16px rgba(0, 105, 92, 0.72);
  color: #00695c;
  transform: translateY(-1px);
}

.client-home-icon-action i {
  color: #c62828;
  font-size: 18px;
  line-height: 18px;
}

.client-mode-actions,
.client-submode-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.client-mode-actions {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(183, 197, 202, 0.72);
  border-radius: 6px;
  gap: 4px;
  padding: 4px;
}

.client-mode-option,
.client-submode-option {
  border-radius: 6px;
  cursor: pointer;
  font-weight: 800;
  min-height: 38px;
  padding: 0 15px;
}

.client-mode-option {
  background: transparent;
  border: 1px solid transparent;
  color: #263238;
}

.client-mode-option:hover {
  background: #eef3f6;
  border-color: #d7e1e5;
}

.client-mode-option.is-active {
  background: #263238;
  border-color: #263238;
  box-shadow: 0 6px 14px -10px rgba(38, 50, 56, 0.75);
  color: #ffffff;
}

.client-submode-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #cfd8dc;
  color: #455a64;
  display: inline-flex;
  gap: 8px;
}

.client-submode-option.is-active {
  border-color: #00897b;
  color: #00695c;
}

.client-submode-option small {
  align-items: center;
  background: #edf5f7;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 800;
  min-width: 24px;
  padding: 2px 7px;
}

.client-secondary-body-frame {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  box-sizing: border-box;
  padding: 26px;
}

.client-secondary-body-frame.is-principal-section {
  background: transparent;
  border: 0;
  box-shadow: none;
  padding: 26px 0 0;
}

.client-secondary-body-frame.is-principal-section.is-principal-detail {
  padding-top: 0;
}

.policy-required-card {
  background: #ffffff;
  border: 1px solid #ffe082;
  border-radius: 6px;
  box-shadow: 0 18px 28px -28px rgba(121, 85, 72, 0.65);
  color: #4e342e;
  padding: 28px;
  text-align: center;
}

.policy-required-card i {
  color: #f9a825;
  font-size: 34px;
}

.policy-required-card h6 {
  color: #263238;
  font-weight: 700;
  margin: 8px 0 6px;
}

.policy-required-card p {
  color: #607d8b;
  margin: 0;
}

.pointable {
  cursor: default;
}

@media only screen and (max-width: 992px) {
  .client-dossier-card,
  .client-mode-strip,
  .client-submode-strip {
    align-items: flex-start;
    flex-direction: column;
  }

  .client-dossier-context {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .client-mode-actions,
  .client-submode-actions {
    justify-content: flex-start;
  }
}

@media only screen and (min-width: 601px) and (max-width: 992px) {
  .retail-secondary-surface > .container {
    width: 92%;
  }

  .client-dossier-card,
  .client-mode-strip,
  .client-submode-strip,
  .client-secondary-body-frame {
    padding: 22px;
  }

  .client-mode-actions,
  .client-submode-actions {
    flex-wrap: nowrap;
    max-width: 100%;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .client-mode-option,
  .client-submode-option {
    flex: 0 0 auto;
  }
}

@media only screen and (max-width: 600px) {
  .client-dossier-card,
  .client-secondary-body-frame {
    padding: 20px;
  }

  .secondary-page-signifier {
    align-items: flex-start;
    border-radius: 6px;
    flex-wrap: wrap;
  }
}
.client-workbench {
  margin-top: 0;
  padding-bottom: 48px;
  padding-top: 0;
  width: 100%;
}

.client-secondary-header {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: 0 20px 34px -32px rgba(38, 50, 56, 0.82);
  margin-bottom: 0;
  position: relative;
}

.client-super-card {
  align-items: center;
  background: #fff8e1;
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 10px 20px -24px rgba(121, 85, 72, 0.55);
  display: flex;
  gap: 10px;
  min-height: 46px;
  padding: 10px 14px;
  position: relative;
  width: 100%;
  z-index: 3;
}

.client-super-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.15;
}

.client-super-copy small {
  color: #6d4c41;
  font-size: 0.72rem;
  font-weight: 700;
}

.client-dossier-card {
  border: 1px solid #c4d3d9;
  border-radius: 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  padding: 26px 34px;
}

.client-mode-strip,
.client-submode-strip {
  border: 1px solid #c4d3d9;
  border-top: 0;
  gap: 18px;
  padding: 12px 16px;
}

.client-mode-strip {
  background: #ffffff;
  border-radius: 0 0 6px 6px;
  box-shadow: none;
}

.client-mode-strip.has-submode {
  border-radius: 0;
}

.client-submode-strip {
  background: #f8fafb;
  border-radius: 0 0 6px 6px;
}

.secondary-page-back {
  background: #00897b;
  border-color: #00796b;
  border-radius: 5px;
  height: 24px;
  width: 24px;
}

.secondary-page-back:hover {
  background: #00695c;
  border-color: #00695c;
}

.secondary-page-back i {
  color: #ffffff;
  font-size: 16px;
  line-height: 16px;
}

.secondary-parent-link {
  color: #4e342e;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.secondary-parent-link:hover {
  color: #00897b;
}

.client-mode-actions {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(183, 197, 202, 0.72);
  border-radius: 6px;
  gap: 4px;
  padding: 4px;
}

.client-mode-option,
.client-submode-option {
  border-radius: 6px;
  cursor: pointer;
  font-weight: 800;
  min-height: 38px;
  padding: 0 15px;
}

.client-mode-option {
  background: transparent;
  border: 1px solid transparent;
  color: #263238;
}

.client-mode-option:hover {
  background: #eef3f6;
  border-color: #d7e1e5;
}

.client-mode-option.is-active {
  background: #263238;
  border-color: #263238;
  box-shadow: 0 6px 14px -10px rgba(38, 50, 56, 0.75);
  color: #ffffff;
}

.client-submode-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #c4d3d9;
  color: #455a64;
  display: inline-flex;
  gap: 8px;
  font-size: 0.8rem;
  letter-spacing: 0;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease;
}

.client-submode-option:hover {
  background: #eef7f7;
  border-color: #80cbc4;
  box-shadow: 0 10px 18px -18px rgba(0, 105, 92, 0.55);
  color: #00695c;
}

.client-submode-option.is-entry-link {
  border-color: #80cbc4;
}

.client-submode-icon {
  align-items: center;
  background: #e0f2f1;
  border-radius: 50%;
  color: #00897b;
  display: inline-flex;
  font-size: 16px;
  height: 24px;
  justify-content: center;
  line-height: 24px;
  margin-left: -5px;
  width: 24px;
}

.client-submode-option.is-entry-link:hover .client-submode-icon,
.client-submode-option.is-entry-link.is-active .client-submode-icon {
  background: #00897b;
  color: #ffffff;
}

.client-submode-option.is-active {
  border-color: #00897b;
  color: #00695c;
}

.client-secondary-body-frame,
.client-secondary-body-frame.is-principal-section {
  background: #f5f7f8;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  margin-top: 14px;
  padding: 16px;
}

.client-secondary-body-frame.is-principal-section.is-principal-detail {
  padding-top: 16px;
}

.client-secondary-body-frame :deep(.principal-card),
.client-secondary-body-frame :deep(.bin-record),
.client-secondary-body-frame :deep(.status-head),
.client-secondary-body-frame :deep(.status-card),
.client-secondary-body-frame :deep(.history-card),
.client-secondary-body-frame :deep(.contract-head),
.client-secondary-body-frame :deep(.contract-card),
.client-secondary-body-frame :deep(.policy-head),
.client-secondary-body-frame :deep(.policy-card),
.client-secondary-body-frame :deep(.benefit-card),
.client-secondary-body-frame :deep(.invoice-head),
.client-secondary-body-frame :deep(.invoice-card),
.client-secondary-body-frame :deep(.invoice-detail),
.client-secondary-body-frame :deep(.detail-loader),
.client-secondary-body-frame :deep(.enrollee-search-bar),
.client-secondary-body-frame :deep(.profile-summary),
.client-secondary-body-frame :deep(.profile-card),
.client-secondary-body-frame :deep(.settings-summary),
.client-secondary-body-frame :deep(.setting-card) {
  border-color: #c4d3d9 !important;
  box-shadow: 0 14px 24px -28px rgba(38, 50, 56, 0.72) !important;
}

.client-secondary-body-frame :deep(.principal-card:hover),
.client-secondary-body-frame :deep(.principal-card:focus-within),
.client-secondary-body-frame :deep(.bin-record:hover),
.client-secondary-body-frame :deep(.bin-record:focus-within),
.client-secondary-body-frame :deep(.policy-card:hover) {
  border-color: #80cbc4 !important;
  box-shadow: 0 16px 26px -26px rgba(0, 105, 92, 0.62) !important;
}

.client-secondary-body-frame :deep(.principal-card-orphan),
.client-secondary-body-frame :deep(.principal-card-orphan:hover),
.client-secondary-body-frame :deep(.principal-card-orphan:focus-within) {
  border-color: #ffcc80 !important;
}

.client-secondary-body-frame :deep(.date-box),
.client-secondary-body-frame :deep(.history-item),
.client-secondary-body-frame :deep(.contract-list-item),
.client-secondary-body-frame :deep(.premium-plan),
.client-secondary-body-frame :deep(.policy-title-row),
.client-secondary-body-frame :deep(.policy-state),
.client-secondary-body-frame :deep(.policy-fact),
.client-secondary-body-frame :deep(.icon-action),
.client-secondary-body-frame :deep(.benefit-head),
.client-secondary-body-frame :deep(.invoice-title-row),
.client-secondary-body-frame :deep(.invoice-period),
.client-secondary-body-frame :deep(.metric-primary),
.client-secondary-body-frame :deep(.metric-secondary),
.client-secondary-body-frame :deep(.status-chip),
.client-secondary-body-frame :deep(.detail-table-wrap table),
.client-secondary-body-frame :deep(.detail-head),
.client-secondary-body-frame :deep(.empty-state),
.client-secondary-body-frame :deep(.summary-item) {
  border-color: #d5e1e5 !important;
}

.client-secondary-body-frame :deep(.policy-fact.premium) {
  border-color: #c4d3d9 !important;
}

.client-secondary-body-frame :deep(.policy-card:hover .policy-state),
.client-secondary-body-frame :deep(.policy-card:hover .policy-fact),
.client-secondary-body-frame :deep(.policy-card:hover .icon-action) {
  border-color: #b4d6d2 !important;
}

@media only screen and (max-width: 600px) {
  .retail-secondary-surface {
    padding-bottom: 34px;
  }

  .client-super-card {
    align-items: flex-start;
    gap: 12px;
  }

  .client-dossier-card,
  .client-secondary-body-frame {
    padding: 20px;
  }

  .client-mode-actions,
  .client-submode-actions {
    flex-wrap: nowrap;
    max-width: 100%;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .client-mode-option,
  .client-submode-option {
    flex: 0 0 auto;
    min-height: 42px;
  }

  .client-secondary-body-frame :deep(.detail-table-wrap),
  .client-secondary-body-frame :deep(.responsive-table) {
    overflow-x: auto;
  }

  .client-secondary-body-frame :deep(table) {
    min-width: 680px;
  }

  .policy-required-card {
    padding: 22px 18px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .client-super-card,
  .client-dossier-card,
  .client-mode-strip,
  .client-submode-strip,
  .client-secondary-body-frame,
  .policy-required-card,
  .client-workbench :deep(.principal-card),
  .client-workbench :deep(.principal-card-orphan),
  .client-workbench :deep(.bin-record),
  .client-workbench :deep(.status-head),
  .client-workbench :deep(.status-card),
  .client-workbench :deep(.history-card),
  .client-workbench :deep(.contract-head),
  .client-workbench :deep(.contract-card),
  .client-workbench :deep(.policy-head),
  .client-workbench :deep(.policy-card),
  .client-workbench :deep(.benefit-card),
  .client-workbench :deep(.invoice-head),
  .client-workbench :deep(.invoice-card),
  .client-workbench :deep(.invoice-detail),
  .client-workbench :deep(.detail-loader),
  .client-workbench :deep(.enrollee-search-bar),
  .client-workbench :deep(.profile-summary),
  .client-workbench :deep(.profile-card),
  .client-workbench :deep(.settings-summary),
  .client-workbench :deep(.setting-card),
  .client-workbench :deep(.empty-state) {
    animation: none;
  }
}
</style>
